/**
 * MobileAppScreen — the phone run surface for a `.miniapp` folder (Mode B) or
 * a standalone HTML file (Mode A). The kind is resolved from the file info
 * (folder+`.miniapp` → miniapp, `.html` file → html) and passed to
 * MiniAppRunView, which serves the folder's index.html or the file directly.
 *
 * Desktop opens a `.miniapp` as a top-level shell window (AppWindow, the
 * `/app/:id` route) that portals its title bar into the shell's permanent
 * top bar. A phone has no shell top-bar host (MobileShell has no
 * ShellTopBarPortal), so this screen supplies its OWN mobile chrome — a
 * secondary bar (back button + app icon + name), same idiom as
 * MobileDriveScreen's breadcrumb bar / MobileSettingsScreen's back — and
 * runs the app in the SAME chromeless runtime the editor's Run tab uses:
 * MiniAppRunView (usePreviewSession mints the sandboxed preview session +
 * install/consent gate; HtmlViewer owns the iframe + SSE reload). None of
 * that runtime needs a workspace/shell provider — it uses only
 * useRealtimeOptional / useToast, both already in MobileShell's inherited
 * provider stack — so it mounts here directly under a ThemedArea.
 *
 * papan.openFile* from the app navigates WITHIN the mobile shell (a file →
 * the standalone viewer, a folder → the drive) instead of the runtime's
 * default new-browser-tab fallback, keeping the phone in one surface.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Settings as SettingsIcon, X } from 'lucide-react'
import { ThemedArea, ErrorBoundary, useShell, type ShellAppKind } from '@papan/core-ui/ui'
import { MiniAppRunView, MiniAppSettingsModifier, type MiniAppRunHandle, type MiniAppNavState, type OpenFileTargetWire, type OpenPdappTargetWire, type OpenFileOptsWire } from '@papan/collab/ui'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { isAppEligibleForFile } from '@papan/share-links/client'
import { filesApi } from '@papan/files/client'
import { useFile } from '@papan/files/client/queries'
import { recordAppOpen } from '@papan/files/ui'
import { useQueryClient } from '@tanstack/react-query'
import { isProjectFolder, presentsAsMiniapp, stripPointerSuffix, parseMiniappIcon, appDisplayName } from '@papan/types'
import { useTenant } from '../hooks/useTenant'
import { persistShellOpen } from '../hooks/useShellPersistence'
import { useAppPanel } from './appPanel'
import { MobileAppControlPanel } from './MobileAppControlPanel'
import { appDeepLinkEntry } from '../lib/app-deep-link'

export function MobileAppScreen() {
  const { fileId } = useParams<{ fileId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // A deep link's page under the app (`/app/<id>/<page>`) and the app's OWN
  // ?query#hash — the desktop route's shape (read the same way as App.tsx's
  // splat), so `pdapp.links.page()`'s Papan address opens the same page here.
  // Papan's own params never reach the app. Latched by MiniAppRunView at mount.
  const { initialPath, initialSuffix } = appDeepLinkEntry(location)
  const { tenantId, isPersonal } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  // Folder detail for the header (name + icon). MiniAppRunView resolves the
  // same info internally but doesn't report it up, so fetch it here for the
  // chrome; the shared cache means it's a hit if the drive just listed it.
  const { data: info } = useFile(fileId)
  const rawName = info?.name ?? ''
  // (#327) Kind-gated so a `.shared` mount of someone else's `.miniapp` runs as an
  // app here too. A pointer is a FILE row presenting as a folder, so the folder
  // test lives inside the predicate rather than on `info.isFolder`.
  const isMiniapp = !!rawName && presentsAsMiniapp(rawName, info?.fileType, info?.isFolder)
  // (#327) App settings edit the SOURCE app's config through native-only routes —
  // hide the surface for a mount (same rule as AppWindow's gear).
  const isMountedApp = info?.fileType === 'shared' || info?.fileType === 'chain'
  // Strip the trailing marker for display AND the shell-store title, so the
  // switcher tile / header read "Foo", not "Foo.miniapp" / "Foo.html" (matches
  // desktop's dock title — App.tsx handleOpenUserApp / RouteSync).
  const title = rawName ? appDisplayName(stripPointerSuffix(rawName)) : 'App'
  // MUST be memoized: parseMiniappIcon returns a fresh object each call, and this
  // feeds the openApp effect below. openApp compares icon BY REFERENCE, so a new
  // object every render makes it setState every render → the effect (which depends
  // on iconSpec) re-runs → openApp → setState → infinite render loop ("Maximum
  // update depth exceeded"). Keyed on the raw metadata string so the ref is stable
  // until the icon actually changes.
  const iconSpec = useMemo(
    () => (info ? parseMiniappIcon(info.metadataJson) : null),
    [info?.metadataJson],
  )
  const kind: ShellAppKind = isMiniapp ? 'miniapp' : 'html'
  // A folder is only a runnable app when it's actually a `.miniapp`. A plain or
  // `.project`/workspace-shell folder (e.g. the `wsi_<hash>_<id>` shell minted by
  // "open .miniapp as workspace", named `<App>.miniapp.project`) that lands on
  // /app/:id must NOT be registered as an app — as a folder it can't run as html.
  // Bounce it to its real surface instead of pinning a broken app tile.
  const isNonAppFolder = !!info && !!info.isFolder && !isMiniapp

  // Register the running app in the SHARED shell store — exactly like desktop's
  // RouteSync (App.tsx:544-548) — so it (1) is tracked as an open user app and
  // (2) surfaces in the mobile app-switcher (MobileNavOverlay) with its icon,
  // reusing the same store desktop's dock reads. Gated on `info` so the kind /
  // title / icon are resolved (folder → 'miniapp', file → 'html') before the
  // entry lands; openApp is idempotent and refreshes title/icon on re-open.
  const { openApp } = useShell()
  // Persist the open server-side ONCE per app (write-through), so a reload
  // rehydrates it via useShellPersistence — mirrors desktop's RouteSync
  // (App.tsx:564). openApp still re-runs on lazy title/icon resolves to refresh
  // the dock chip, but the PATCH fires only on first registration.
  const persistedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!fileId || !info || isNonAppFolder) return
    openApp(kind, fileId, title, isMiniapp ? iconSpec : undefined)
    if (!persistedRef.current.has(fileId)) {
      persistedRef.current.add(fileId)
      void persistShellOpen(kind, fileId, `${prefix}/app/${fileId}`).catch(() => {})
    }
  }, [fileId, info, isNonAppFolder, kind, title, isMiniapp, iconSpec, openApp, prefix])

  // #1484: a PDApp opened here goes to the person's PDApp history — once per
  // arrival (this screen mounts per visit). A loose HTML file is not a PDApp.
  // The server checks the app is readable before it stores the open.
  const queryClient = useQueryClient()
  const recordedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!fileId || !info || isNonAppFolder || !isMiniapp || recordedRef.current === fileId) return
    recordedRef.current = fileId
    recordAppOpen(queryClient, fileId)
  }, [fileId, info, isNonAppFolder, isMiniapp, queryClient])

  // Redirect a non-app folder to its real surface (workspace view for a
  // `.project` shell, drive folder view otherwise).
  useEffect(() => {
    if (!fileId || !isNonAppFolder) return
    navigate(
      isProjectFolder(rawName) ? `${prefix}/workspace/${fileId}` : `${prefix}/folder/${fileId}`,
      { replace: true },
    )
  }, [fileId, isNonAppFolder, rawName, prefix, navigate])

  // papan.openFile* → navigate IN the mobile shell (matches how the desktop
  // app window opens targets in-app rather than a raw new browser tab). Same
  // resolution as MiniAppRunView's fallback, but router navigation instead of
  // window.open — fire-and-forget, no oracle back to the app on failure.
  /** #788 — `papan.openPdappByFolderId()`: LAUNCH the named app. Mobile mounts
   *  real routes, so this is a plain navigation to the app route, which resolves
   *  and GATES the target as the viewer. Fire-and-forget, no oracle back. A
   *  target in another tenant needs that tenant's prefix. */
  const handleOpenPdappTarget = useCallback((target: OpenPdappTargetWire) => {
    const to = target.tenantId === tenantId
      ? `${prefix}/app/${encodeURIComponent(target.folderId)}`
      : `/t/${encodeURIComponent(target.tenantId)}/app/${encodeURIComponent(target.folderId)}`
    navigate(to)
  }, [navigate, prefix, tenantId])

  const handleOpenFileTarget = useCallback(async (
    target: OpenFileTargetWire,
    ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
  ) => {
    try {
      let id: string
      let isFolder = false
      if (target.kind === 'fileId') {
        id = target.fileId
        // Resolve isFolder for the correct route (viewer vs drive).
        // The shared resolver (one retry; `null` when it cannot tell) — mobile was
        // the surface that always got this right, and it should not be the one
        // left guessing per call site. See `isFolderById`.
        isFolder = (await filesApi.isFolderById(id)) ?? false
      } else {
        const result = target.kind === 'relativePath'
          ? await filesApi.resolvePath(target.path, ctx.appFolderId ?? undefined)
          : await filesApi.resolvePath(`/papan/${target.path.replace(/^\/+/, '')}`)
        if (!result?.id) return
        id = result.id
        isFolder = result.file.isFolder
      }
      navigate(isFolder ? `${prefix}/folder/${id}` : `${prefix}/file/${id}`)
    } catch { /* not found / no access — swallow */ }
  }, [navigate, prefix])

  // ── Running-app controls (header cutout → glass control panel) ──────────
  // The trigger is the header cutout (MobileHeader flips this shared signal
  // when an app is running); the panel is rendered HERE because this screen
  // holds the runtime ref + owns the settings/share overlays.
  const { open: appPanelOpen, setOpen: setAppPanelOpen } = useAppPanel()
  const runRef = useRef<MiniAppRunHandle>(null)
  const NAV_RESET: MiniAppNavState = { hasNavigated: false, canBack: false, canForward: false }
  const [navState, setNavState] = useState<MiniAppNavState>(NAV_RESET)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  // Controls only make sense for a real running app (not a bounced folder).
  const isRunnable = !!info && !isNonAppFolder

  // Reset control state when the app changes; close the panel when the screen
  // unmounts (navigating off /app) so the shared signal can't linger open.
  useEffect(() => {
    setNavState(NAV_RESET); setSettingsOpen(false); setShareOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId])
  useEffect(() => () => setAppPanelOpen(false), [setAppPanelOpen])

  // Refresh/Home — SOFT reload (HtmlViewer's double-buffered swap via the run
  // handle): reloads the entry page with no remount / session re-mint / flash.
  // The entry load re-reports nav state, but reset optimistically so the
  // control flips to Refresh (back/forward disabled) immediately.
  const reloadApp = useCallback(() => { setNavState(NAV_RESET); runRef.current?.reload() }, [])

  if (!fileId) return null

  return (
    <ThemedArea area="workspace" className="relative flex-1 flex min-w-0 min-h-0 flex-col ws-clear">
      {/* The app runtime — chromeless runner, full-bleed. The app's identity
          (icon · "MINIAPP" · title) lives in the shell header's context pill,
          so no secondary bar here. */}
      <div className="flex-1 flex min-w-0 min-h-0 flex-col overflow-hidden">
        <ErrorBoundary>
          {/* `key`: one runner per app. Every /app/… route renders this same screen, so without it
              a move to another app (openPdappByFolderId) would reuse the runner and carry the
              first app's deep-linked page + query + hash (latched at mount) into the next one. */}
          <MiniAppRunView
            key={fileId}
            ref={runRef}
            folderId={fileId}
            folderName={isMiniapp ? title : undefined}
            kind={isMiniapp ? 'miniapp' : 'html'}
            onNavStateChange={setNavState}
            onOpenFileTarget={handleOpenFileTarget}
            onOpenPdappTarget={handleOpenPdappTarget}
            initialPath={initialPath}
            initialSuffix={initialSuffix}
            tenantId={tenantId}
          />
        </ErrorBoundary>
      </div>

      {/* Header cutout → glass control panel. HTML: back/forward/refresh-home/
          share; mini-app: refresh/settings/share. */}
      {appPanelOpen && isRunnable && (
        <MobileAppControlPanel
          kind={isMiniapp ? 'miniapp' : 'html'}
          hasNavigated={navState.hasNavigated}
          canBack={navState.canBack}
          canForward={navState.canForward}
          onBack={() => runRef.current?.back()}
          onForward={() => runRef.current?.forward()}
          onReloadHome={reloadApp}
          onSettings={() => setSettingsOpen(true)}
          onShare={() => setShareOpen(true)}
          onClose={() => setAppPanelOpen(false)}
        />
      )}

      {/* In-place mini-app settings (Mode B) — the same modifier AppWindow's
          gear opens, as a full-screen overlay over the running app. */}
      {settingsOpen && isMiniapp && !isMountedApp && (
        <div className="absolute inset-0 z-[120] flex flex-col themed-bg-primary">
          <div className="flex h-11 shrink-0 items-center justify-between border-b themed-border px-3">
            <span className="flex items-center gap-1.5 text-sm font-medium themed-text">
              <SettingsIcon size={14} /> {title} · Settings
            </span>
            <button
              onClick={() => setSettingsOpen(false)}
              aria-label="Close settings"
              className="flex h-8 w-8 items-center justify-center rounded-lg themed-icon active:themed-bg-tertiary"
            >
              <X size={16} />
            </button>
          </div>
          {/* Flex column, not block — see the note at AppWindow's gear overlay:
              the modifier's `flex-1 overflow-y-auto` root cannot scroll unless
              this parent is a flex container. */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <MiniAppSettingsModifier folderId={fileId} folderName={title} />
          </div>
        </div>
      )}

      {/* Share the running app — app-eligible for a mini-app, plain file share
          for standalone HTML. */}
      {shareOpen && (
        <ShareLinksDialog
          fileId={fileId}
          fileName={rawName}
          filePath={(info as { path?: string } | undefined)?.path ?? `/papan/${rawName}`}
          isFolder={!!info?.isFolder}
          // App-mode link is eligible for BOTH a `.miniapp` folder AND a loose
          // `.html` file (shareKindForFile → 'app'), not just mini-apps.
          isAppEligible={isAppEligibleForFile({ name: rawName, isFolder: !!info?.isFolder })}
          onClose={() => setShareOpen(false)}
        />
      )}
    </ThemedArea>
  )
}
